// frontend/src/pages/PaychequeList/index.tsx
// Thin orchestrator: state, data wiring, derived values.
// Delegates heavy JSX to sub-components, mutations to usePaychequeMutations,
// and standard states (loading/error/empty) + CSV export to shared infra.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronDown,
  MoreVertical,
  Printer,
  RotateCcw,
  FileText,
  X as XIcon,
} from "lucide-react";

import type {
  Paycheque,
  CompanyInfo,
  TabType,
  SortType,
  MethodFilter,
} from "./types";
import { API_URL, fmtDate } from "./helpers";
import {
  FONT,
  C,
  outlineBtnLarger,
  footerBtn,
  menuItem,
  thStyle,
  tabStyle,
} from "./styles";
import { usePaycheques, useCompanySettings } from "./usePaycheques";
import { usePaychequeMutations } from "./useMutations";

import PaychequeRow from "./components/PaychequeRow";
import VoidConfirmModal from "./components/VoidConfirmModal";
import ChequeModal from "./components/ChequeModal";
import FilterMenu from "./components/FilterMenu";

import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { exportToCsv } from "../../utils/csv";

// Re-export shared types for backwards compatibility
export type { PaychequeStatus, Paycheque } from "./types";

export default function PaychequeList(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── State ───────────────────────────────────────────────────────────────
  const [paycheques, setPaycheques] = useState<Paycheque[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabType>("all");
  const [search, setSearch] = useState<string>("");
  const [sort, setSort] = useState<SortType>("newest");
  const [methodFilter, setMethodFilter] = useState<MethodFilter>("all");
  const [privacy, setPrivacy] = useState<boolean>(
    localStorage.getItem("novala_privacy") === "on"
  );
  const [exportOpen, setExportOpen] = useState<boolean>(false);
  const [moreOpen, setMoreOpen] = useState<boolean>(false);
  const [openKebabId, setOpenKebabId] = useState<string | null>(null);
  const [editingChq, setEditingChq] = useState<string | null>(null);
  const [chqInput, setChqInput] = useState<string>("");
  const [chequeModal, setChequeModal] = useState<Paycheque | null>(null);
  const [voidTarget, setVoidTarget] = useState<Paycheque | null>(null);
  const [voidReason, setVoidReason] = useState<string>("");
  const [company, setCompany] = useState<CompanyInfo>({ name: "", address: "" });

  const exportRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // ── Mutations ───────────────────────────────────────────────────────────
  const {
    saveChequeNumber: saveChequeNumberMutation,
    voidPaycheque: voidPaychequeMutation,
    savingChq,
    voiding,
  } = usePaychequeMutations();

  // ── Effects ─────────────────────────────────────────────────────────────
  useEffect(function () {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (exportRef.current && !exportRef.current.contains(target)) setExportOpen(false);
      if (moreRef.current && !moreRef.current.contains(target)) setMoreOpen(false);
      if (!(e.target as HTMLElement)?.closest?.(".row-kebab")) setOpenKebabId(null);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setExportOpen(false); setMoreOpen(false); setOpenKebabId(null);
        setEditingChq(null); setChequeModal(null);
      }
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return function () {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // ── Data ────────────────────────────────────────────────────────────────
  const {
    data: paychequesData,
    isLoading: paychequesLoading,
    error: paychequesError,
  } = usePaycheques();

  useEffect(function () {
    if (paychequesData) setPaycheques(paychequesData);
    setLoading(paychequesLoading);
    if (paychequesError) setError((paychequesError as Error).message);
  }, [paychequesData, paychequesLoading, paychequesError]);

  function fetchPaycheques() {
    queryClient.invalidateQueries({ queryKey: ["paycheques"] });
  }

  const { data: companyData } = useCompanySettings();

  useEffect(function () {
    if (companyData) {
      setCompany({
        name: companyData.company_name || localStorage.getItem("company_name") || "",
        address: companyData.company_address || "",
      });
    }
  }, [companyData]);

  // ── Mutation side-effect wrappers (hook returns result; parent updates UI) ──
  async function handleSaveCheque(stubId: string, value: string): Promise<void> {
    const result = await saveChequeNumberMutation(stubId, value);
    if (!result.success) {
      alert("Could not save: " + result.error);
      return;
    }
    setPaycheques((prev) =>
      prev.map((p) => (p.id === stubId ? { ...p, cheque_number: result.chequeNumber ?? null } : p))
    );
    setEditingChq(null);
    setChqInput("");
  }

  async function handleVoid(stubId: string, reason: string): Promise<void> {
    const result = await voidPaychequeMutation(stubId, reason);
    if (!result.success) {
      alert("Could not void: " + result.error);
      return;
    }
    setVoidTarget(null);
    setVoidReason("");
    fetchPaycheques();
  }

  // ── Derived values ──────────────────────────────────────────────────────
  function togglePrivacy() {
    const next = !privacy;
    setPrivacy(next);
    localStorage.setItem("novala_privacy", next ? "on" : "off");
  }

  const counts = useMemo(function () {
    return {
      all: paycheques.length,
      issued: paycheques.filter((p) => p.status === "issued").length,
      pending: paycheques.filter((p) => (p.status || "pending") === "pending").length,
      paid: paycheques.filter((p) => p.status === "paid").length,
      voided: paycheques.filter((p) => p.status === "voided").length,
    };
  }, [paycheques]);

  const filtered = useMemo(function () {
    let list = paycheques.slice();
    if (tab !== "all") list = list.filter((p) => (p.status || "pending") === tab);
    if (methodFilter !== "all") {
      list = list.filter((p) => {
        const m = (p.payment_method || p.pay_method || "").toLowerCase();
        if (methodFilter === "cheque") return m.includes("cheque") || m.includes("check");
        if (methodFilter === "direct") return m.includes("direct") || m.includes("deposit");
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => {
        const name = (p.employee_name || "").toLowerCase();
        const chq = String(p.cheque_number || "").toLowerCase();
        return name.includes(q) || chq.includes(q);
      });
    }
    if (sort === "newest") list.sort((a, b) => new Date(b.pay_date || 0).getTime() - new Date(a.pay_date || 0).getTime());
    else if (sort === "oldest") list.sort((a, b) => new Date(a.pay_date || 0).getTime() - new Date(b.pay_date || 0).getTime());
    else if (sort === "net-high") list.sort((a, b) => Number(b.net_pay || 0) - Number(a.net_pay || 0));
    else if (sort === "net-low") list.sort((a, b) => Number(a.net_pay || 0) - Number(b.net_pay || 0));
    else if (sort === "name-az") list.sort((a, b) => (a.employee_name || "").localeCompare(b.employee_name || ""));
    return list;
  }, [paycheques, tab, methodFilter, search, sort]);

  const sectionTitle =
    tab === "pending" ? "Pending paycheques" :
    tab === "issued" ? "Issued paycheques" :
    tab === "paid" ? "Paid paycheques" :
    tab === "voided" ? "Voided paycheques" :
    "Paycheque history";

  function printPayStub(p: Paycheque): void {
    setOpenKebabId(null);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    const pdfUrl = API_URL + "/api/v1/payroll/paycheques/" + p.id + "/pdf?token=" + encodeURIComponent(token);
    window.open(pdfUrl, "_blank");
  }

  function handleExportCSV(): void {
    const headers = ["Pay date", "Employee", "Gross pay", "Net pay", "Method", "Cheque/Ref", "Status"];
    const rows = filtered.map((p) => [
      fmtDate(p.pay_date),
      p.employee_name || "",
      Number(p.gross_pay || 0).toFixed(2),
      Number(p.net_pay || 0).toFixed(2),
      p.payment_method || p.pay_method || "",
      p.cheque_number || "",
      p.status || "pending",
    ]);
    exportToCsv("paycheques_" + new Date().toISOString().slice(0, 10), headers, rows);
    setExportOpen(false);
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ background: C.page, minHeight: "100vh", fontFamily: FONT, color: C.ink, padding: "28px 32px 100px", boxSizing: "border-box" }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: C.ink, marginBottom: 14 }}>
        <span style={{ fontWeight: 600, opacity: 0.7, cursor: "pointer" }} onClick={() => navigate("/payroll/overview")}>Payroll</span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ fontWeight: 700 }}>Paycheques</span>
      </div>

      {/* Title + top-right actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em" }}>Paycheques</h1>
          <div style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 4 }}>All employee paycheques from finalized pay runs.</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
          <button onClick={togglePrivacy} style={Object.assign({}, outlineBtnLarger, { background: privacy ? C.brandBg : "transparent", borderColor: privacy ? C.brand : C.ink })}>
            <div style={{ width: 32, height: 18, borderRadius: 10, background: privacy ? C.brand : "#D1D5DB", position: "relative", transition: "0.2s" }}>
              <div style={{ position: "absolute", top: 2, left: privacy ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "0.2s" }} />
            </div>
            {privacy ? "Privacy on" : "Privacy"}
          </button>

          <div ref={exportRef} style={{ position: "relative" }}>
            <button onClick={() => { setExportOpen(!exportOpen); setMoreOpen(false); }} style={outlineBtnLarger}>Export <ChevronDown size={12} /></button>
            {exportOpen && (
              <div style={{ position: "absolute", top: 46, right: 0, background: C.card, border: "1px solid " + C.line, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: "6px 0", minWidth: 200, zIndex: 100 }}>
                <button onClick={handleExportCSV} style={menuItem}><FileText size={14} /> Export to CSV</button>
                <button onClick={() => { window.print(); setExportOpen(false); }} style={menuItem}><Printer size={14} /> Save as PDF</button>
              </div>
            )}
          </div>

          <div ref={moreRef} style={{ position: "relative" }}>
            <button onClick={() => { setMoreOpen(!moreOpen); setExportOpen(false); }} style={{ width: 40, height: 40, background: C.card, border: "1.5px solid " + C.ink, borderRadius: 10, color: C.ink, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <MoreVertical size={16} />
            </button>
            {moreOpen && (
              <div style={{ position: "absolute", top: 46, right: 0, background: C.card, border: "1px solid " + C.line, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: "6px 0", minWidth: 220, zIndex: 100 }}>
                <button disabled style={Object.assign({}, menuItem, { opacity: 0.4, cursor: "not-allowed" })}><RotateCcw size={14} /> Bulk void selected</button>
                <button onClick={() => { setMoreOpen(false); navigate("/tools/audit-log"); }} style={menuItem}><FileText size={14} /> View audit log</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 20, maxWidth: 500 }}>
        <Search size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.ink }} />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by employee name or cheque number..."
          style={{ width: "100%", padding: "12px 14px 12px 44px", border: "1.5px solid " + C.ink, borderRadius: 10, fontFamily: FONT, fontSize: 14, color: C.ink, background: C.card, fontWeight: 500, boxSizing: "border-box", outline: "none" }}
        />
        {search && (
          <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 22, height: 22, borderRadius: "50%", background: C.ink, color: "#fff", border: "none", cursor: "pointer", display: "grid", placeItems: "center", padding: 0 }}>
            <XIcon size={14} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid " + C.line }}>
        <button onClick={() => setTab("all")} style={tabStyle(tab === "all")}>All <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.all}</span></button>
        <button onClick={() => setTab("pending")} style={tabStyle(tab === "pending")}>Pending <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.pending}</span></button>
        <button onClick={() => setTab("issued")} style={tabStyle(tab === "issued")}>Issued <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.issued}</span></button>
        <button onClick={() => setTab("paid")} style={tabStyle(tab === "paid")}>Paid <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.paid}</span></button>
        <button onClick={() => setTab("voided")} style={tabStyle(tab === "voided")}>Voided <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>{counts.voided}</span></button>
      </div>

      {/* Section header + filter */}
      <div style={{ paddingBottom: 8, borderBottom: "1.5px solid " + C.ink, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>{sectionTitle}</h2>
        <FilterMenu
          sort={sort}
          onSortChange={setSort}
          methodFilter={methodFilter}
          onMethodFilterChange={setMethodFilter}
        />
      </div>

      {/* States */}
      {loading && <LoadingState message="Loading paycheques..." color={C.ink} />}

      {error && !loading && <ErrorState message={error} onRetry={fetchPaycheques} />}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={<FileText size={48} />}
          title={search ? "No matches" : paycheques.length === 0 ? "No paycheques yet" : "Nothing to show"}
          description={
            search ? ('No paycheques match "' + search + '".') :
            paycheques.length === 0 ? "Paycheques appear here after a pay run is finalized." :
            "Try switching tabs or adjusting filters."
          }
          fontFamily={FONT}
          color={C.ink}
        />
      )}

      {/* Table */}
      {!loading && !error && filtered.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={thStyle}><input type="checkbox" style={{ cursor: "pointer" }} /></th>
              <th style={thStyle}>PAY DATE</th>
              <th style={thStyle}>EMPLOYEE</th>
              <th style={{ ...thStyle, textAlign: "right" }}>GROSS PAY</th>
              <th style={{ ...thStyle, textAlign: "right" }}>NET PAY</th>
              <th style={thStyle}>METHOD</th>
              <th style={thStyle}>CHEQUE / REF NO</th>
              <th style={thStyle}>STATUS</th>
              <th style={{ ...thStyle, textAlign: "right", width: 60 }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <PaychequeRow
                key={p.id}
                paycheque={p}
                privacy={privacy}
                isEditingCheque={editingChq === p.id}
                chequeInput={chqInput}
                savingCheque={savingChq}
                isKebabOpen={openKebabId === p.id}
                onNavigateToDetail={() => navigate("/payroll/paycheques/" + p.id)}
                onPrintPayStub={() => printPayStub(p)}
                onOpenCheque={() => { setChequeModal(p); setOpenKebabId(null); }}
                onStartChequeEdit={() => { setEditingChq(p.id); setChqInput(""); }}
                onChequeInputChange={setChqInput}
                onSaveCheque={() => handleSaveCheque(p.id, chqInput)}
                onCancelChequeEdit={() => { setEditingChq(null); setChqInput(""); }}
                onToggleKebab={() => setOpenKebabId(openKebabId === p.id ? null : p.id)}
                onCloseKebab={() => setOpenKebabId(null)}
                onRequestVoid={() => { setVoidTarget(p); setVoidReason(""); }}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Modals */}
      {voidTarget && (
        <VoidConfirmModal
          target={voidTarget}
          reason={voidReason}
          voiding={voiding}
          onReasonChange={setVoidReason}
          onCancel={() => setVoidTarget(null)}
          onConfirm={() => handleVoid(voidTarget.id, voidReason)}
        />
      )}

      {chequeModal && (
        <ChequeModal
          target={chequeModal}
          company={company}
          onClose={() => setChequeModal(null)}
        />
      )}

      {/* Fixed footer */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.card, padding: "16px 32px", borderTop: "1px solid " + C.line, boxShadow: "0 -4px 12px rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 90 }}>
        <button onClick={() => navigate("/payroll/overview")} style={footerBtn}>&larr; Back to Payroll</button>
        <button onClick={handleExportCSV} style={footerBtn}>Export all</button>
      </div>
    </div>
  );
}